import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { initializeAnalytics } from "@/lib/analytics";

const CONSENT_KEY = "cookie-consent";

type ConsentStatus = "accepted" | "rejected" | null;

export function CookieConsent() {
  const [consentStatus, setConsentStatus] = useState<ConsentStatus>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(CONSENT_KEY) as ConsentStatus;
    if (stored) {
      setConsentStatus(stored);
      if (stored === "accepted") {
        initializeAnalytics();
      }
    } else {
      setIsVisible(true);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem(CONSENT_KEY, "accepted");
    setConsentStatus("accepted");
    setIsVisible(false);
    initializeAnalytics();
  };

  const handleReject = () => {
    localStorage.setItem(CONSENT_KEY, "rejected");
    setConsentStatus("rejected");
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-sm z-[60] mb-[calc(env(safe-area-inset-bottom)+4.5rem)] sm:mb-0 p-3 sm:p-4 bg-surface-elevated/95 backdrop-blur-sm border border-border/50 rounded-2xl shadow-lg animate-in slide-in-from-bottom duration-300">
      <div className="flex flex-col gap-3">
        <p className="text-xs sm:text-sm text-muted-foreground">
          We use cookies for analytics.{" "}
          <Link to="/privacy" className="text-primary hover:underline">
            Read our privacy policy
          </Link>
        </p>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleReject}
            className="text-xs text-muted-foreground hover:text-foreground flex-1 sm:flex-none"
          >
            Reject
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={handleAccept}
            className="text-xs flex-1 sm:flex-none"
          >
            Accept
          </Button>
        </div>
      </div>
    </div>
  );
}

// Utility to check consent status
export function hasAnalyticsConsent(): boolean {
  if (typeof window === "undefined") return false;
  return localStorage.getItem(CONSENT_KEY) === "accepted";
}
